import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import {
  EMPTY, Observable, Subject, catchError, concatMap, filter, finalize, fromEvent,
  merge, of, shareReplay, takeUntil, tap, timer,
} from 'rxjs';
import { SessionStorageService } from './app-session.service';
import { CurrentUserService } from './current-user.service';
import { CurrentUser, CurrentUserModule } from '../interface/current-user.interface';
import { PermissionNavigationItem } from '../constants/app-navigation.constants';
import { PERMISSION_ACTIONS } from '../constants/module-permissions.constant';

/**
 * Action-level permissions for a single module, resolved lazily on every read so the flags stay
 * correct even when permissions finish loading after the component is created.
 */
export interface ModulePermissions {
  /** Any action other than Add/Edit/View/Delete, e.g. `can('Approve')`. */
  can(actionName: string): boolean;
  readonly hasAccess: boolean;
  readonly add: boolean;
  readonly edit: boolean;
  readonly view: boolean;
  readonly viewPage: boolean;
  readonly delete: boolean;
  readonly save: boolean;
  readonly sendEmail: boolean;
  readonly import: boolean;
  readonly exportToExcel: boolean;
  readonly download: boolean;
  readonly revokeOnboard: boolean;
  readonly arrivedAtJetty: boolean;
  readonly revokeTo: boolean;
  readonly removeCargo: boolean;
  readonly postponeCargo: boolean;
  readonly saveForLoadingList: boolean;
  readonly moveTo: boolean;
  readonly allMaterialRequests: boolean;
  readonly viewAllMaterialRequest: boolean;
  readonly removeCargoFromCancelledLocation: boolean;
  readonly removeLocation: boolean;
  readonly addVSIT: boolean;
  readonly exportLoadingList: boolean;
  readonly exportChevronCargoManifest: boolean;
  readonly exportDGcargoManifest: boolean;
}

/**
 * Single source of truth for the logged-in user's module/action permissions.
 *
 * Loads effective permissions from `Auth/GetCurrentUser` and keeps the signal synchronized
 * after management changes, every 30 seconds while visible, and when returning to the tab.
 * Persisted snapshots are never trusted to authorize a newly loaded application.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly session = inject(SessionStorageService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userStorageKey = 'currentUserPermissions';
  private readonly refreshRequested$ = new Subject<void>();
  private readonly stopped$ = new Subject<void>();
  private synchronizing = false;

  /** In-flight load, shared so concurrent guards/components trigger a single HTTP call. */
  private loadInFlight$: Observable<CurrentUser> | null = null;

  /** One `ModulePermissions` view per module, shared across components. */
  private readonly modulePermissionsCache = new Map<string, ModulePermissions>();

  private readonly userSignal = signal<CurrentUser | null>(null);

  /** Read-only signal of the current user's permission snapshot. */
  readonly user = this.userSignal.asReadonly();

  /** True once permissions have been loaded for the current session. */
  readonly permissionsLoaded = computed(() => this.userSignal() !== null);

  /**
   * Loads the current user's permissions if not already cached for this session.
   * @returns An observable that emits the cached or freshly-loaded `CurrentUser`.
   */
  ensureLoaded(): Observable<CurrentUser> {
    const existing = this.userSignal();
    if (existing) {
      return of(existing);
    }

    return this.refresh();
  }

  /** Fetches a fresh snapshot, sharing concurrent reads and allowing retries after failure. */
  refresh(): Observable<CurrentUser> {
    if (this.loadInFlight$) {
      return this.loadInFlight$;
    }
    const release = () => {
      if (this.loadInFlight$ === request$) {
        this.loadInFlight$ = null;
      }
    };
    const request$ = this.currentUserService.getCurrentUser(false).pipe(
      takeUntil(this.stopped$),
      tap({ next: (user) => this.storeUser(user), complete: release, error: release }),
      finalize(release),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.loadInFlight$ = request$;
    return request$;
  }

  /** Starts once sign-in is complete; management saves also refresh hidden tabs. */
  startSynchronization(): void {
    const window = this.document.defaultView;
    if (this.synchronizing || !window) {
      return;
    }
    this.synchronizing = true;
    const visible$ = merge(
      timer(0, 30_000),
      fromEvent(window, 'focus'),
      fromEvent(this.document, 'visibilitychange'),
    ).pipe(filter(() => this.document.visibilityState === 'visible'));

    merge(visible$, this.refreshRequested$).pipe(
      // Queue a save behind an outstanding read so it cannot reuse a pre-save response.
      concatMap(() => this.refresh().pipe(
        catchError((error: unknown) => {
          // The HTTP interceptor logs/notifies failures; later ticks retry transient errors.
          if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            this.clearUser();
          }
          return EMPTY;
        }),
      )),
      takeUntil(this.stopped$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  /** Called only after a successful permission-affecting write. */
  requestRefresh(): void {
    this.refreshRequested$.next();
  }

  /** Stores the loaded user permissions in session storage and the in-memory signal. */
  storeUser(user: CurrentUser): void {
    this.session.setItem(this.userStorageKey, user);
    this.userSignal.set(user);
  }

  /** Clears cached permissions, e.g. on logout or role/permission change. */
  clearUser(): void {
    this.stopped$.next();
    this.synchronizing = false;
    this.session.removeItem(this.userStorageKey);
    this.userSignal.set(null);
    this.loadInFlight$ = null;
  }

  getUser(): CurrentUser | null {
    return this.userSignal();
  }

  getModule(moduleName: string): CurrentUserModule | null {
    return this.getUser()?.modules.find((module) => module.moduleName === moduleName) ?? null;
  }

  /**
   * Legacy module access requires at least one selected action, including ViewPage or SendEmail.
   * No action vetoes another selection; individual action permissions remain independent.
   */
  hasModuleAccess(moduleName: string): boolean {
    const module = this.getModule(moduleName);
    if (!module) {
      return false;
    }
    return module.permissions.some((permission) => permission.isSelected);
  }

  /** Operation-level rule, e.g. hasAction('UserManagement', 'Add'). */
  hasAction(moduleName: string, actionName: string): boolean {
    const module = this.getModule(moduleName);
    return (
      module?.permissions.some(
        (permission) => permission.isSelected && permission.actionName === actionName,
      ) ?? false
    );
  }

  /**
   * Returns the action flags for a module so a component declares its permissions in one line:
   * `protected readonly permissions = inject(PermissionService).forModule(PERMISSION_MODULES.X);`
   * @param moduleName Permission module name, from `PERMISSION_MODULES`.
   * @returns A cached, lazily-evaluated view of that module's action permissions.
   */
  forModule(moduleName: string): ModulePermissions {
    const cached = this.modulePermissionsCache.get(moduleName);
    if (cached) {
      return cached;
    }

    const can = (actionName: string) => this.hasAction(moduleName, actionName);
    const hasModuleAccess = () => this.hasModuleAccess(moduleName);
    const permissions: ModulePermissions = {
      can,
      get hasAccess() {
        return hasModuleAccess();
      },
      get add() {
        return can(PERMISSION_ACTIONS.ADD);
      },
      get edit() {
        return can(PERMISSION_ACTIONS.EDIT);
      },
      get view() {
        return can(PERMISSION_ACTIONS.VIEW);
      },
      get viewPage() {
        return can(PERMISSION_ACTIONS.VIEW_PAGE);
      },
      get delete() {
        return can(PERMISSION_ACTIONS.DELETE);
      },
      get save() {
        return can(PERMISSION_ACTIONS.SAVE);
      },
      get sendEmail() {
        return can(PERMISSION_ACTIONS.SEND_EMAIL);
      },
      get import() {
        return can(PERMISSION_ACTIONS.IMPORT);
      },
      get exportToExcel() {
        return can(PERMISSION_ACTIONS.EXPORT_TO_EXCEL);
      },  
      get download() {
        return can(PERMISSION_ACTIONS.DOWNLOAD);
      },
      get revokeOnboard() {
        return can(PERMISSION_ACTIONS.REVOKE_ONBOARD);
      },
      get arrivedAtJetty() {
        return can(PERMISSION_ACTIONS.ArrivedAtJetty);
      },
      get revokeTo() {
        return can(PERMISSION_ACTIONS.RevokeTo);
      },
      get removeCargo() {
        return can(PERMISSION_ACTIONS.RemoveCargo);
      },
      get postponeCargo() {
        return can(PERMISSION_ACTIONS.PostponeCargo);
      },
      get saveForLoadingList() {
        return can(PERMISSION_ACTIONS.SaveForLoadingList);
      },
      get moveTo() {
        return can(PERMISSION_ACTIONS.MoveTo);
      },
      get allMaterialRequests() {
        return can(PERMISSION_ACTIONS.AllMaterialRequests);
      },
      get viewAllMaterialRequest() {
        return can(PERMISSION_ACTIONS.VIEW_ALL_MATERIAL_REQUEST);
      },
      get removeCargoFromCancelledLocation() {
        return can(PERMISSION_ACTIONS.REMOVE_CARGO_FROM_CANCELLED_LOCATION);
      },
      get removeLocation() {
        return can(PERMISSION_ACTIONS.RemoveLocation);
      },
      get addVSIT() {
        return can(PERMISSION_ACTIONS.AddVSIT);
      },
      get exportLoadingList() {
        return can(PERMISSION_ACTIONS.ExportLoadingList);
      },
      get exportChevronCargoManifest() {
        return can(PERMISSION_ACTIONS.ExportChevronCargoManifest);
      },
      get exportDGcargoManifest() {
        return can(PERMISSION_ACTIONS.ExportDGcargoManifest);
      },
    };

    this.modulePermissionsCache.set(moduleName, permissions);
    return permissions;
  }

  /**
   * Recursively filters a navigation tree down to items the current user may see, applying the
   * legacy rule at every level (1, 2, and 3+):
   *  - A group (has `children`) is visible only when at least one descendant remains visible.
   *  - A leaf with `permissionModule` requires at least one selected action in that module.
   *  - A leaf without `permissionModule` is not yet migrated and stays visible (opt-in rollout).
   */
  filterNavigationItems(items: PermissionNavigationItem[]): PermissionNavigationItem[] {
    return items.reduce<PermissionNavigationItem[]>((visible, item) => {
      const isGroup = Boolean(item.children && item.children.length > 0);
      const filteredChildren = isGroup
        ? this.filterNavigationItems(item.children as PermissionNavigationItem[])
        : undefined;
      const hasVisibleChildren = Boolean(filteredChildren && filteredChildren.length > 0);
      const isVisible = this.isNavigationItemVisible(item, isGroup, hasVisibleChildren);

      if (!isVisible) {
        return visible;
      }

      visible.push({
        ...item,
        ...(filteredChildren ? { children: filteredChildren } : {}),
      });
      return visible;
    }, []);
  }

  private isNavigationItemVisible(
    item: PermissionNavigationItem,
    isGroup: boolean,
    hasVisibleChildren: boolean,
  ): boolean {
    if (isGroup) {
      return hasVisibleChildren;
    }
    if (item.permissionModule) {
      return this.hasModuleAccess(item.permissionModule);
    }
    return true;
  }
}
